import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  ajvResolver,
  ChangeLog,
  MenuItems,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  usePrevious,
  ValidatorsProps,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useBlockCreditMutation,
  useBlockDebitMutation,
  useCloseAccountMutation,
  useCreateAccountMutation,
  useLazyGetAccountBalancesQuery,
  useLazyGetAccountChangeLogsQuery,
  useLazyGetAccountQuery,
  useOpenAccountMutation,
  useUnblockCreditMutation,
  useUnblockDebitMutation,
  useUpdateAccountMutation,
  useUpdateOverdraftBalanceValueMutation,
  useUpdateOverdraftStatusMutation,
  useLazyGetAccountClosingBalancesQuery,
} from '../../store/services';
import { Account, AccountFormValues } from '../../types';
import { Balance } from '../../../statements/types';
import { clearAccoutState } from '../../store/slice';
import { setNotificationVisible } from '../../../../../../../store/slice';

import ROUTES from '../../../../routes';

const useFormFunctions = ({
  accountId,
  validators,
  account,
  setPanelIsOpen,
  changeLogs,
  activePermissions,
  search,
  menuItems,
  categoryId,
  groupId,
  primaryId,
  balances,
  overdraftEnabledSetting,
  closingBalancesAt,
}: {
  accountId?: string;
  validators?: {
    createAccountSchema: ValidatorsProps;
  };
  account?: Account;
  setPanelIsOpen: (data: boolean) => void;
  changeLogs?: ChangeLog[];
  activePermissions?: PermissionEnum[];
  search?: string;
  menuItems?: MenuItems[];
  categoryId: string | null;
  groupId: string | null;
  primaryId: string | null;
  balances?: Balance[];
  overdraftEnabledSetting?: boolean;
  closingBalancesAt?: string;
}) => {
  const {
    control,
    reset,
    watch,
    handleSubmit,
    setError,
    formState: { errors, isDirty },
  } = useForm<AccountFormValues>({
    resolver: ajvResolver(validators?.createAccountSchema as ValidatorsProps),
  });

  const [blockUblockCreditError, setBlockUblockCreditError] = useState('');
  const [blockUblockDebitError, setBlockUblockDebitError] = useState('');
  const [closeOpenError, setCloseOpenError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [clientAccountError, setClientAccountError] = useState('');
  const [creditBlock, setCreditBlock] = useState(false);
  const [debitBlock, setDebitBlock] = useState(false);
  const [creditBlockConfirmationVisible, setCreditBlockConfirmationVisible] =
    useState(false);
  const [debitBlockConfirmationVisible, setDebitBlockConfirmationVisible] =
    useState(false);
  const [commentPopupVisible, setCommentPopupVisible] = useState(false);
  const [comment, setComment] = useState('');
  const [availableCurrenciesVisible, setAvailableCurrenciesVisible] =
    useState(false);
  const [overdraftEnabled, setOverdraftEnabled] = useState(false);
  const [accountBalances, setAccountBalances] = useState<Balance[]>();
  const [defaultBalances, setDefautBalances] = useState<Balance[]>();
  const [activeTab, setActiveTab] = useState<'details' | 'logs'>('details');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const prevClosingAt = usePrevious(closingBalancesAt);

  const nextIsDirty = isDirty || !isEqual(defaultBalances, accountBalances);

  const description = watch('description');
  const revaluable = watch('revaluable');

  const [create, { isLoading, isSuccess }] = useCreateAccountMutation();
  const [update, { isLoading: updateInProgress }] = useUpdateAccountMutation();
  const [
    blockCredit,
    { isLoading: creditBlockInProgress, isSuccess: creditBlockIsSuccess },
  ] = useBlockCreditMutation();
  const [
    unblockCredit,
    { isLoading: creditUnblockInProgress, isSuccess: creditUnblockIsSuccess },
  ] = useUnblockCreditMutation();
  const [
    blockDebit,
    { isLoading: debitBlockInProgress, isSuccess: debitBlockIsSuccess },
  ] = useBlockDebitMutation();
  const [
    unblockDebit,
    { isLoading: debitUnblockInProgress, isSuccess: debitUnblockIsSuccess },
  ] = useUnblockDebitMutation();
  const [close, { isLoading: closeInProgress, isSuccess: closeIsSuccess }] =
    useCloseAccountMutation();
  const [open, { isLoading: openInProgress, isSuccess: openIsSuccess }] =
    useOpenAccountMutation();
  const [getAccount, { isLoading: accountLoading, isFetching }] =
    useLazyGetAccountQuery();
  const [getAccountBalances, { isLoading: accountBalancesLoading }] =
    useLazyGetAccountBalancesQuery();
  const [updateOverdraftStatus, { isLoading: overdraftUpdateInProgress }] =
    useUpdateOverdraftStatusMutation();
  const [
    updateOverdraftBalanceValue,
    { isLoading: updateOverdraftBalanceIsLoading },
  ] = useUpdateOverdraftBalanceValueMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetAccountChangeLogsQuery();
  const [
    getAccountClosingBalances,
    { isLoading: closingBalancesLoading, isFetching: closingBalancesFetching },
  ] = useLazyGetAccountClosingBalancesQuery();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ACCOUNT&entityId=${accountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [accountId],
  );

  const getAccountBalancesAction = useCallback(() => {
    getAccountBalances(accountId as string);
  }, [accountId]);

  const getAccountAction = useCallback(async () => {
    const body: {
      id: string;
    } = {
      id: accountId as string,
    };

    const response = await getAccount(body);

    if (response) {
      if (
        closingBalancesAt &&
        prevClosingAt !== undefined &&
        closingBalancesAt !== prevClosingAt
      ) {
        await getAccountClosingBalances(
          `?accountId=${accountId}&date=${closingBalancesAt}`,
        );
      }
    }
  }, [accountId, closingBalancesAt, prevClosingAt]);

  const onUpdateOverdraftStatus = async (enabled: boolean) => {
    setOverdraftEnabled(enabled);

    const response = (await updateOverdraftStatus({
      id: accountId as string,
      overdraftEnabled: enabled,
    })) as { error?: ResponseError };

    if (response.error) {
      setOverdraftEnabled(!enabled);
    }
  };

  const onBlockCredit = async (status: boolean) => {
    if (status) {
      const response = (await blockCredit(accountId as string)) as {
        error?: { data: { message: string[] } };
      };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockCreditError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await unblockCredit(accountId as string)) as {
        error?: { data: { message: string[] } };
      };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockCreditError(response.error?.data?.message[0]);
      }
    }
  };

  const onBlockDebit = async (status: boolean) => {
    if (status) {
      const response = (await blockDebit(accountId as string)) as {
        error?: { data: { message: string[] } };
      };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockDebitError(response.error?.data?.message[0]);
      }
    } else {
      const response = (await unblockDebit(accountId as string)) as {
        error?: { data: { message: string[] } };
      };

      if (response.error?.data?.message && response.error?.data?.message[0]) {
        setBlockUblockDebitError(response.error?.data?.message[0]);
      }
    }
  };

  const onUpdate = handleSubmit(async (values) => {
    if (
      !isEqual(
        {
          revaluable: account?.revaluable,
          description: account?.description,
        },
        { revaluable: values.revaluable, description: values.description },
      )
    ) {
      update({
        id: accountId as string,
        revaluable: values.revaluable,
        description: values.description,
      });
    }

    if (!isEqual(defaultBalances, accountBalances)) {
      const updatedBalances: Balance[] = [];

      accountBalances?.forEach((item) => {
        const defaultBalance = defaultBalances?.find(
          (balance) => balance.id === item.id,
        );

        if (defaultBalance && !isEqual(defaultBalance, item)) {
          updatedBalances.push(item);
        }
      });

      if (updatedBalances.length) {
        Promise.all(
          updatedBalances?.map((item) => {
            const response = updateOverdraftBalanceValue({
              id: accountId as string,
              balanceId: item.id,
              overdraftEnabled: item.overdraftEnabled as boolean,
              overdraftValue: item.overdraftValue as number,
            });

            return response;
          }),
        );
      }
    }
  });

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const response = (await create({
      description: values.description,
      createdAt: values.createdAt,
      primaryId: values.primaryId,
      creditBlock,
      debitBlock,
    })) as {
      data?: { data: { ok: boolean } };
      error?: ResponseError;
    };

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });

      if (
        response?.error?.data?.message &&
        values.fullNumber?.includes('3.0.')
      ) {
        setClientAccountError(response?.error?.data?.message as string);
      }
    }
  });

  const onClose = async () => {
    if (account?.closed) {
      const response = (await open({
        body: {
          comment,
        },
        id: accountId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message) {
        if (Array.isArray(response.error?.data?.message)) {
          setCloseOpenError(response.error?.data?.message[0]);
        } else {
          setCloseOpenError(response.error?.data?.message);
        }
      }
    } else {
      const response = (await close({
        body: {
          comment,
        },
        id: accountId as string,
      })) as { error?: { data: { message: string[] } } };

      if (response.error?.data?.message) {
        if (Array.isArray(response.error?.data?.message)) {
          setCloseOpenError(response.error?.data?.message[0]);
        } else {
          setCloseOpenError(response.error?.data?.message);
        }
      }
    }
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    reset({});
    dispatch(clearAccoutState());

    setTimeout(() => {
      navigate(`${ROUTES.ACCOUNTS_LIST}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    setPanelIsOpen(true);

    if (accountId) {
      getAccountAction();
      getAccountBalancesAction();
    }
  }, [accountId, getAccountAction, getAccountBalancesAction]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Account creation in progress. You'll be notified.",
          type: 'accounts-create',
        }),
      );
    }
  }, [isSuccess]);

  useEffect(() => {
    if (closeIsSuccess || openIsSuccess) {
      setComment('');
      setCommentPopupVisible(false);
    }
  }, [openIsSuccess, closeIsSuccess]);

  useEffect(() => {
    if (categoryId && groupId && primaryId && menuItems) {
      const currentCategory = menuItems?.find(
        (item) => item.category.id === categoryId,
      );

      if (currentCategory) {
        const currentGroup = currentCategory.category.groups?.find(
          (item) => item.id === groupId,
        );

        if (currentGroup) {
          const currentPrimaryAccount = currentGroup.accounts?.find(
            (item) => item.id === primaryId,
          );

          if (currentPrimaryAccount) {
            reset({
              categoryName: currentCategory.category.label,
              groupName: currentGroup.label,
              primaryId,
              primaryAccountName: currentPrimaryAccount?.label,
              description: '',
              createdAt: new Date().toISOString(),
              fullNumber: `${currentCategory.category.number}.${currentGroup.number}.${currentPrimaryAccount.number}.XXXXXX`,
              type: currentCategory.category.type,
            });
          }
        }
      }
    }
  }, [categoryId, groupId, primaryId, menuItems, reset]);

  useEffect(() => {
    if (account && menuItems && accountId) {
      setOverdraftEnabled(account.overdraftEnabled as boolean);

      const currentCategory = menuItems?.find(
        (item) => item.category.id === account.categoryId,
      );

      if (currentCategory) {
        const currentGroup = currentCategory.category.groups?.find(
          (item) => item.id === account.groupId,
        );

        if (currentGroup) {
          const currentPrimaryAccount = currentGroup.accounts?.find(
            (item) => item.id === account.primaryId,
          );

          if (currentPrimaryAccount) {
            reset({
              categoryName: currentCategory.category.label,
              groupName: currentGroup.label,
              primaryId: account.primaryId,
              primaryAccountName: currentPrimaryAccount?.label,
              description: account.description,
              createdAt: account.createdAt,
              fullNumber: account.fullNumber,
              type: account.accountType,
              revaluable: account.revaluable,
            });

            setCreditBlock(account.creditBlock);
            setDebitBlock(account.debitBlock);
          }
        }
      }
    }
  }, [account, menuItems, reset]);

  useEffect(() => {
    if (creditBlockIsSuccess || creditUnblockIsSuccess) {
      setCreditBlockConfirmationVisible(false);
    }
  }, [creditBlockIsSuccess, creditUnblockIsSuccess]);

  useEffect(() => {
    if (debitBlockIsSuccess || debitUnblockIsSuccess) {
      setDebitBlockConfirmationVisible(false);
    }
  }, [debitBlockIsSuccess, debitUnblockIsSuccess]);

  useEffect(() => {
    if (balances) {
      let nextBalances = balances;

      if (overdraftEnabledSetting) {
        nextBalances = balances?.map((item) => {
          const result = { ...item };

          if (result.overdraftValue === null) {
            result.overdraftValue = 0;
            result.overdraftEnabled = false;
          }

          return result;
        });
      }

      setAccountBalances(nextBalances);
      setDefautBalances(nextBalances);
    }
  }, [balances, overdraftEnabledSetting]);

  return {
    control,
    reset,
    errors,
    blockUblockCreditError,
    blockUblockDebitError,
    closeOpenError,
    nextIsDirty,
    description,
    revaluable,
    globalError,
    clientAccountError,
    isLoading,
    updateInProgress,
    creditBlockInProgress,
    creditUnblockInProgress,
    debitBlockInProgress,
    debitUnblockInProgress,
    closeInProgress,
    openInProgress,
    accountLoading,
    isFetching,
    accountBalancesLoading,
    overdraftUpdateInProgress,
    updateOverdraftBalanceIsLoading,
    changeLogsIsLoading,
    changeLogsIsFetching,
    onUpdateOverdraftStatus,
    onBlockCredit,
    onBlockDebit,
    onUpdate,
    onSubmit,
    onClose,
    getChangeLogsAction,
    creditBlock,
    setCreditBlock,
    debitBlock,
    setDebitBlock,
    creditBlockConfirmationVisible,
    setCreditBlockConfirmationVisible,
    debitBlockConfirmationVisible,
    setDebitBlockConfirmationVisible,
    commentPopupVisible,
    setCommentPopupVisible,
    comment,
    setComment,
    availableCurrenciesVisible,
    setAvailableCurrenciesVisible,
    overdraftEnabled,
    accountBalances,
    setAccountBalances,
    defaultBalances,
    activeTab,
    setActiveTab,
    handleClose,
    closingBalancesLoading: closingBalancesFetching || closingBalancesLoading,
  };
};

export default useFormFunctions;
